import { Helmet } from '@dr.pogodin/react-helmet';
import { useEffect, useRef } from 'react';
import { useCookies } from 'react-cookie';
import ReactGA from 'react-ga4';
import { useLocation } from 'react-router';
import Config, { type MultiLang } from '../config';
import Functions from '../functions';
import CenterColumn from './CenterColumn';
import Header from './Header';
import LeftColumn from './LeftColumn';

const isAnalyticsEnabled = import.meta.env.PROD && Config.GOOGLE_ANALYTICS_TRACKING_ID !== '';

if (isAnalyticsEnabled) {
    // Page views are sent from the location effect below, the first one included;
    // gtag's own page_view on config would count the landing page twice.
    ReactGA.initialize(Config.GOOGLE_ANALYTICS_TRACKING_ID, { gtagOptions: { send_page_view: false } });
}

const toMultiLang = (value: unknown): MultiLang | null => (value === 'en' || value === 'ja' ? value : null);

const AppRoot = () => {
    const location = useLocation();
    const [cookies, setCookie] = useCookies(['ml_lang']);
    const previousLocation = useRef<string | null>(null);

    const paramLang = toMultiLang(new URLSearchParams(location.search).get('ml_lang'));
    const cookieLang = toMultiLang(cookies.ml_lang);
    const lang: MultiLang = paramLang ?? cookieLang ?? 'en';

    useEffect(() => {
        if (cookies.ml_lang !== lang) {
            setCookie('ml_lang', lang, { path: '/' });
        }
    }, [cookies.ml_lang, lang, setCookie]);

    useEffect(() => {
        if (isAnalyticsEnabled) {
            ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search });
        }
        const current = location.pathname + location.search;
        if (previousLocation.current !== null && previousLocation.current !== current) {
            window.scrollTo(0, 0);
        }
        previousLocation.current = current;
    }, [location.pathname, location.search]);

    return (
        <>
            <Helmet>
                <title>
                    {Functions.siteTitle(lang)} - {Functions.siteSlogan(lang)}
                </title>
            </Helmet>
            <Header lang={lang} />
            <div id="main" className="clearfix">
                <LeftColumn lang={lang} />
                <CenterColumn lang={lang} />
            </div>
        </>
    );
};

export default AppRoot;
